import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {InfoIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useRef} from 'react'
import {clsx} from 'clsx'
import {OverviewSidePanel} from './OverviewSidePanel'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {useDescriptionLiveUpdates} from '../../hooks/use-description-live-updates'
import {useDescriptionData} from '../../page-data/loaders/use-description-data'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {DockedPanel, useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'

/**
 * Renders a button that opens the overview panel
 */
export function OpenOverviewPanelButton({
  aliveChannel,
  className,
  size = 'small',
}: {
  aliveChannel: string
  className?: string
  size?: 'small' | 'medium'
}) {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.Overview)
  const actions = useDockedPanelActions()

  const togglePanelRef = useRef<HTMLButtonElement>(null)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const overviewButtonAriaLabel = isDockedPanelOpen ? 'Close overview panel' : 'Open overview panel'

  // eslint-disable-next-line github/unescaped-html-literal, @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe
  const emptyDescription = `<p class="text-italic">No description provided.</p>` as SafeHTMLString

  const {data: description, isLoading} = useDescriptionData()
  useDescriptionLiveUpdates(aliveChannel)
  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
  const bodyHtml = (description?.bodyHtml || emptyDescription) as SafeHTMLString

  return (
    <div className={clsx('d-flex flex-items-center', className)}>
      <ErrorBoundary
        boundaryName="OpenOverviewPanelButton"
        fallback={
          <IconButton
            inactive
            aria-label="The overview panel cannot currently be opened."
            icon={InfoIcon}
            size={size}
            variant="invisible"
          />
        }
      >
        <IconButton
          ref={togglePanelRef}
          aria-label={overviewButtonAriaLabel}
          aria-expanded={isDockedPanelOpen}
          icon={InfoIcon}
          size={size}
          variant="invisible"
          onClick={() => {
            sendPullRequestAnalyticsEvent('overview_side_panel.open', 'OVERVIEW_SIDE_PANEL_BUTTON')
            actions.togglePanel('overview', {returnFocusTo: togglePanelRef.current})
          }}
        />
        <OverviewSidePanel
          bodyHtml={bodyHtml}
          isLoading={isLoading}
          isOpen={isDockedPanelOpen}
          toggleSidesheetRef={togglePanelRef}
          onClose={actions.closePanel}
        />
      </ErrorBoundary>
    </div>
  )
}
